import { twMerge } from 'tailwind-merge';

import { useQueryData } from '@/sections/insights/queryBuilder/state';
import type { FlatGraphEdge } from '@/types/FlatGraph';

import { EdgeDetails } from './EdgeCard/EdgeDetails';
import { EdgeLabel } from './EdgeCard/EdgeLabel';

interface EdgeCardProps {
  edge: FlatGraphEdge;
  isExpanded: boolean;
  onToggle: (edgeId: string) => void;
  editable?: boolean;
  onUpdateLabel?: (edgeId: string, newLabels: string[]) => void;
  onUpdateEdgeData?: (
    edgeId: string,
    data: Partial<FlatGraphEdge['data']>
  ) => void;
}

/**
 * EdgeCard - Collapsible card displaying a query relationship
 * Orchestrates three subcomponents: EdgeLabel, EdgeDetails, EdgeAddNode
 */
export const EdgeCard = ({
  edge,
  isExpanded,
  onToggle,
  editable = false,
  onUpdateLabel,
  onUpdateEdgeData,
}: EdgeCardProps) => {
  // Get flatGraph from context to lookup source/target nodes
  const { queryState } = useQueryData();

  // Guard against null queryState
  if (!queryState) {
    return null;
  }

  const { flatGraph } = queryState;

  // Look up source and target nodes
  const sourceNode = flatGraph.nodes.get(edge.sourceId);
  const targetNode = flatGraph.nodes.get(edge.targetId);

  // Guard against missing nodes
  if (!sourceNode || !targetNode) {
    console.error('EdgeCard: Missing source or target node', {
      edgeId: edge.id,
      sourceId: edge.sourceId,
      targetId: edge.targetId,
    });
    return null;
  }

  const labels = Array.isArray(edge.data.label)
    ? edge.data.label
    : [edge.data.label];
  const labelText = labels.join(', ');

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
      <EdgeLabel
        edge={edge}
        sourceNode={sourceNode}
        targetNode={targetNode}
        isExpanded={isExpanded}
        editable={editable}
        onToggle={onToggle}
        onUpdateLabel={onUpdateLabel}
      />

      {/* Expandable content with CSS Grid collapse */}
      <div
        id={`edge-content-${edge.id}`}
        role="region"
        aria-label={`${labelText} relationship details`}
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <EdgeDetails
            edge={edge}
            editable={editable}
            onUpdateEdgeData={onUpdateEdgeData}
          />
        </div>
      </div>
    </div>
  );
};
